import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface GroupAssessmentNavigationProps {
  currentGroupIndex: number;
  totalGroups: number;
  canGoPreviousGroup: boolean;
  canGoNextGroup: boolean;
  onPreviousGroup: () => void;
  onNextGroup: () => void;
  onSubmit: (isAutoSubmit: boolean) => void;
  isLastGroup: boolean;
  isSubmitting: boolean;
  isSavingResponses: boolean;
  currentGroupName?: string;
  nextGroupName?: string;
}

export const GroupAssessmentNavigation = React.memo(
  ({
    currentGroupIndex,
    totalGroups,
    canGoPreviousGroup,
    canGoNextGroup,
    onPreviousGroup,
    onNextGroup,
    onSubmit,
    isLastGroup,
    isSubmitting,
    isSavingResponses,
    currentGroupName,
    nextGroupName,
  }: GroupAssessmentNavigationProps) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const isBusy = isSubmitting || isSavingResponses;

    const handleSubmitClick = () => {
      setShowConfirmDialog(true);
    };

    const handleConfirmSubmit = () => {
      setShowConfirmDialog(false);
      onSubmit(false);
    };

    return (
      <>
        <div className="flex justify-between items-center pt-6 border-t bg-white rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={onPreviousGroup}
              disabled={!canGoPreviousGroup || isBusy}
              size="sm"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous Section
            </Button>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-600">
              Section {currentGroupIndex + 1} of {totalGroups}
            </div>
            {currentGroupName && (
              <div className="text-xs text-gray-500 mt-1">{currentGroupName}</div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {isLastGroup ? (
                <Button
                  onClick={handleSubmitClick}
                  disabled={isBusy}
                  size="sm"
                >
                  {isSubmitting
                    ? "Submitting..."
                    : isSavingResponses
                    ? "Saving..."
                    : "Submit Assessment"}
                </Button>
              ) : (
                <Button
                  onClick={onNextGroup}
                  disabled={!canGoNextGroup || isBusy}
                  size="sm"
                >
                  {isSavingResponses ? "Saving..." : "Next Section"}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
          </div>
        </div>

        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Confirm Assessment Submission
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3 pt-2">
                <p className="font-medium text-foreground">
                  Are you sure you want to submit your assessment?
                </p>
                <p>
                  Once submitted, you will not be able to make any changes to your answers.
                  Please review your responses before proceeding.
                </p>
                <p className="text-amber-600 font-medium">
                  This action cannot be undone.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isBusy}>
                Review Answers
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmSubmit}
                disabled={isBusy}
                className="bg-primary hover:bg-primary/90"
              >
                {isSubmitting
                  ? "Submitting..."
                  : isSavingResponses
                  ? "Saving..."
                  : "Yes, Submit Assessment"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }
);

GroupAssessmentNavigation.displayName = "GroupAssessmentNavigation";
